import ReactMarkdown from 'react-markdown';
import {
  ChangeEventHandler,
  FC,
  useEffect,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { translate } from '@/translation/translate.ts';
import { ConfigField, ConfigSlideId } from '@/eventConfig/types.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { useFieldValidation } from '../_pipeline/useValidateField';

interface SwitchProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const SwitchInput: FC<SwitchProps> = ({ field, slideId, getFieldStatus, onChange, getFieldValue }) => {
  const fieldStatus = getFieldStatus(field.name);
  const { handleUiEvent } = useUiEvent();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);

  useEffect(() => {
    // Commit each checkbox as value "false" when initially rendered
    const value = fieldStatus?.value?.raw ?? false;
    const fieldValue = { raw: value, normalized: value };
    onChange(field.name, { value: fieldValue, isValid: true, hint: undefined, slideId });
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const options = field.options ?? [];
  const colWidth = options.length > 0 ? Math.floor(12 / options.length) : 12;

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
  ];

  const hint = fromTemplate(fieldHint || field.hint || '');

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        {options.map((switchDefinition, idx) => {
          const { left, right, width, name, click } = switchDefinition;
          const cssClasses = ['toggle-option', 'col-' + (width || colWidth)];

          const handleSwitchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (e.target.checked) {
              const fieldValue = {
                raw: switchDefinition.value,
                normalized: switchDefinition.value,
              };
              const { hint, isValid } = validateField(field.validator, fieldValue);
              onChange(field.name, { value: fieldValue, hint, isValid, slideId });
            } else {
              const isbool = typeof switchDefinition.value === 'boolean';
              const notCheckedValue = isbool ? !switchDefinition.value : false;

              const fieldValue = {
                raw: notCheckedValue,
                normalized: notCheckedValue,
              };

              const { hint, isValid } = validateField(field.validator, fieldValue);
              onChange(field.name, { value: fieldValue, hint, isValid, slideId });
            }
          };

          const handleSwitchLabelActivate = (
            e: ReactMouseEvent<HTMLLabelElement> | ReactKeyboardEvent<HTMLLabelElement>,
            targetSwitchId: string
          ) => {
            // Keyboard activation: only act on Space/Enter
            if ('key' in e) {
              const key = e.key;
              const isActivation = key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter';
              if (!isActivation) {
                return; // Do nothing on Tab/Arrow/etc.
              }
              e.preventDefault();
              const input = document.getElementById(targetSwitchId) as HTMLInputElement | null;
              input?.click();
              (e.currentTarget as HTMLLabelElement).focus();
              if (click && handleUiEvent && isValid) {
                handleUiEvent(click);
              }
              return;
            }

            // Mouse click: treat as activation (label click toggles input by default)
            if (click && handleUiEvent && isValid) {
              handleUiEvent(click);
            }
          };

          const switchId = htmlId + '_' + idx + '_' + switchDefinition.name;

          return (
            <div className={cssClasses.join(' ')} key={name}>
              {left && (
                <div className="left">
                  <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
                    {translate(left)}
                  </ReactMarkdown>
                </div>
              )}

              <input
                type="checkbox"
                name={name}
                id={switchId}
                checked={String(switchDefinition.value) == value}
                value={String(switchDefinition.value)}
                onChange={handleSwitchChange}
                tabIndex={-1}
              />

              <label
                id={`${switchId}_label`}
                htmlFor={switchId}
                className="aligned"
                role="switch"
                aria-checked={String(switchDefinition.value) == value}
                tabIndex={0}
                onClick={(e) => {
                  handleSwitchLabelActivate(e, switchId);
                  (e.currentTarget as HTMLLabelElement).focus();
                }}
                onKeyDown={(e) => {
                  const key = e.key;
                  if (key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter') {
                    handleSwitchLabelActivate(e, switchId);
                  }
                }}
              >
                <ReactMarkdown unwrapDisallowed={true}>{'toggle'}</ReactMarkdown>
              </label>
              {right && (
                <div className="right">
                  <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
                    {translate(right)}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
